import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Temporal } from '@js-temporal/polyfill';
import { useState, type FormEvent } from 'react';

import {
  agreementQuery,
  allocationQuery,
  type AllocationTerms,
  bpToPercent,
  LINE_TYPE_LABELS,
  LINE_TYPES,
  percentToBp,
  saveAgreement,
  SETTLEMENT_LABELS,
  SUPPLY_MODEL_LABELS,
  type Agreement,
  type AgreementInput,
  type AgreementSupplyModel,
  type LineType,
  type Settlement,
} from '../api/agreements.ts';
import { clientsQuery, createClient } from '../api/clients.ts';
import { jobsQuery } from '../api/jobs.ts';
import { dayWithYear, tenantDay } from '../api/quotes.ts';
import { updateVenue, venueQuery, type Venue } from '../api/venues.ts';
import { overdueInvoicesQuery, venueJobsQuery, venueQuotesQuery } from '../api/venue-page.ts';
import { monthLabel, shiftMonth, type Statement, statementQuery } from '../api/statements.ts';
import { Meter } from '../components/facts.tsx';
import { Popover } from '../components/popover.tsx';
import { span } from '../venue-week.ts';
import { EditButton } from '../components/action-buttons.tsx';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { dollarsToMinorUnits, formatMinorUnits, minorUnitsToDollars } from '../money.ts';

// CB-REQ VEN-11 and VEN-01 at v1.988 (venue slice 4A): who a venue is invoiced as, and its
// agreement. The agreement's margins are money, so the venue page shows it only to someone who can
// see prices, and offers Edit only to someone who may manage venues and prices; the database checks
// the save itself. Each save is a new version, listed under History.

export function VenueBilling({
  venue,
  capabilities,
  zone,
}: {
  venue: Venue;
  capabilities: readonly string[];
  zone: string | undefined;
}) {
  const may = (key: string) => capabilities.includes(key);
  const mayManage = may('venues.manage');
  const agreement = useQuery({ ...agreementQuery(venue.id), enabled: may('prices.view') });
  const live = agreement.data?.live ?? null;
  const statementKind =
    live?.settlement === 'monthly' &&
    (live.supplyModel === 'resale' || live.supplyModel === 'direct_bill')
      ? live.supplyModel
      : null;
  return (
    <div className="flex flex-col gap-4">
      {/* VEN-01 at v1.9104, the operator's Money board: ex GST, above the price line. */}
      {may('prices.view') ? (
        <MoneyStats
          venue={venue}
          capabilities={capabilities}
          zone={zone}
          statementKind={statementKind}
        />
      ) : null}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        {may('prices.view') ? (
          <AgreementSection
            venue={venue}
            mayManage={mayManage}
            mayEdit={mayManage && may('prices.manage')}
            zone={zone}
          />
        ) : (
          <section className="panel flex flex-col gap-2 p-4">
            <h2 className="panel-label">Debtor</h2>
            <BilledAs venue={venue} mayManage={mayManage} />
          </section>
        )}
        <div className="flex flex-col gap-4">
          {live?.allocation ? <AllocationBalancePanel venueId={venue.id} zone={zone} /> : null}
          {statementKind && may('invoices.view') && may('prices.view') ? (
            <VenueStatements
              venueId={venue.id}
              kind={statementKind}
              zone={zone}
              mayRecord={may('invoices.manage')}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** "$412,600" from minor units: the board's figures are whole dollars. */
const wholeDollars = (minor: bigint, currency: string) =>
  formatMinorUnits(((minor / 100n) * 100n).toString(), currency).replace(/\.00$/, '');

const thisMonth = (zone: string | undefined) =>
  Temporal.Now.plainDateISO(zone ?? 'Australia/Melbourne')
    .toPlainYearMonth()
    .toString();

const owedOn = (s: Statement) =>
  s.resale ? s.resale.balanceOwingMinor : (s.directBill?.totalOwedMinor ?? '0');

/**
 * The board's figures, each only for someone who may read it: sold over 12 months (the quotes in
 * force of the jobs here, since JOB-01 carries no money), what the venue owes this month (its
 * statement, VEN-11), and its overdue invoices. The board's *Kept after venue share* is not drawn:
 * nothing reads the margin across jobs yet.
 */
function MoneyStats({
  venue: v,
  capabilities,
  zone,
  statementKind,
}: {
  venue: Venue;
  capabilities: readonly string[];
  zone: string | undefined;
  statementKind: 'resale' | 'direct_bill' | null;
}) {
  const may = (key: string) => capabilities.includes(key);
  const year = span(Temporal.Now.instant(), -365);
  const seesSold = may('jobs.view') && may('quotes.view');
  const jobs = useQuery({ ...venueJobsQuery(v.id, year.from, year.to), enabled: seesSold });
  const quotes = useQuery({ ...venueQuotesQuery(v.id, year.from, year.to), enabled: seesSold });
  const month = thisMonth(zone);
  const statement = useQuery({
    ...statementQuery(v.id, month, statementKind ?? 'resale'),
    enabled: !!statementKind && may('invoices.view'),
    retry: false,
  });
  const overdue = useQuery({ ...overdueInvoicesQuery(v.id), enabled: may('invoices.view') });

  const stats: Array<[string, string, boolean]> = [];
  if (seesSold && jobs.data && quotes.data) {
    const inForce = new Set(
      jobs.data.filter((j) => j.status !== 'cancelled').map((j) => j.quoteId),
    );
    const totals = quotes.data.filter((q) => inForce.has(q.id) && q.total);
    const minor = totals.reduce((n, q) => n + BigInt(q.total!.amountMinor), 0n);
    stats.push([
      wholeDollars(minor, totals[0]?.total?.currency ?? 'AUD'),
      'Sold · 12 months',
      false,
    ]);
  }
  if (statementKind && may('invoices.view') && statement.data) {
    stats.push([
      formatMinorUnits(owedOn(statement.data), 'AUD'),
      `Owed by venue · ${monthLabel(month).slice(0, 3)}`,
      false,
    ]);
  }
  if (may('invoices.view') && overdue.data)
    stats.push([String(overdue.data.length), 'Overdue', overdue.data.length > 0]);
  if (stats.length === 0) return null;
  return (
    <dl className="venue-stats flex-wrap">
      {stats.map(([value, label, bad]) => (
        <div key={label} className="venue-stat min-w-40 flex-1">
          <dt>{label}</dt>
          <dd className={bad ? 'text-accent-2-700' : undefined}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * VEN-11 at v1.990: the venue's last three monthly statements, each opening its page, where a
 * direct-bill venue's own invoice is recorded.
 */
function VenueStatements({
  venueId,
  kind,
  zone,
  mayRecord,
}: {
  venueId: string;
  kind: 'resale' | 'direct_bill';
  zone: string | undefined;
  mayRecord: boolean;
}) {
  const month = thisMonth(zone);
  const months = [month, shiftMonth(month, -1), shiftMonth(month, -2)];
  return (
    <section className="panel flex flex-col gap-2 p-4" aria-labelledby="statements">
      <div className="flex items-center gap-2">
        <h2 id="statements" className="panel-label flex-1">
          Statements
        </h2>
        {kind === 'direct_bill' && mayRecord ? (
          <Link
            to="/statements/$venueId/$month"
            params={{ venueId, month }}
            search={{ kind }}
            className="link text-sm"
          >
            Record venue invoice
          </Link>
        ) : null}
      </div>
      <table className="data-table text-sm">
        <caption className="sr-only">The last three months’ statements</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col" className="text-right">
              Invoices
            </th>
            <th scope="col" className="text-right">
              {kind === 'resale' ? 'Owing' : 'Owed'}
            </th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <StatementRow key={m} venueId={venueId} month={m} kind={kind} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function StatementRow({
  venueId,
  month,
  kind,
}: {
  venueId: string;
  month: string;
  kind: 'resale' | 'direct_bill';
}) {
  const statement = useQuery({ ...statementQuery(venueId, month, kind), retry: false });
  const s = statement.data;
  return (
    <tr>
      <th scope="row">
        <Link
          to="/statements/$venueId/$month"
          params={{ venueId, month }}
          search={{ kind }}
          className="link"
        >
          {monthLabel(month)}
        </Link>
      </th>
      <td className="text-right tabular-nums">
        {s ? (s.resale ? s.resale.lines.length : (s.directBill?.lines.length ?? 0)) : '—'}
      </td>
      <td className="text-right tabular-nums">{s ? formatMinorUnits(owedOn(s), 'AUD') : '—'}</td>
    </tr>
  );
}

function BilledAs({ venue, mayManage }: { venue: Venue; mayManage: boolean }) {
  const queryClient = useQueryClient();
  const clients = useQuery(clientsQuery);
  const organisations = (clients.data ?? []).filter((c) => c.kind === 'organisation');
  const current = organisations.find((c) => c.id === venue.billedAsClientId);
  const set = useMutation({
    mutationFn: async (choice: string) => {
      // "+ New organisation" makes one from the venue's own name, on account, then links it.
      const id =
        choice === '+'
          ? (
              await createClient({
                kind: 'organisation',
                name: venue.name,
                paymentTerms: 'account',
                accountDays: 30,
              })
            ).id
          : choice || null;
      return updateVenue(venue.id, { billedAsClientId: id });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: venueQuery(venue.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: clientsQuery.queryKey });
    },
  });

  return (
    <div className="flex flex-col gap-1">
      {mayManage ? (
        <label className="flex max-w-md flex-col gap-1.5">
          <span className="sr-only">Debtor</span>
          <select
            className="input"
            value={venue.billedAsClientId ?? ''}
            disabled={set.isPending}
            onChange={(e) => set.mutate(e.currentTarget.value)}
          >
            <option value="">None</option>
            {organisations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
            <option value="+">+ New organisation</option>
          </select>
        </label>
      ) : (
        <span>{current?.name ?? 'None'}</span>
      )}
      {set.isError ? (
        <p role="alert" className="alert">
          {set.error.message}
        </p>
      ) : null}
    </div>
  );
}

function AgreementSection({
  venue,
  mayManage,
  mayEdit,
  zone,
}: {
  venue: Venue;
  mayManage: boolean;
  mayEdit: boolean;
  zone: string | undefined;
}) {
  const agreement = useQuery(agreementQuery(venue.id));
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const live = agreement.data?.live ?? null;
  const history = agreement.data?.history ?? [];

  return (
    <section className="panel flex flex-col gap-3 p-4" aria-labelledby="agreement">
      <div className="flex items-center gap-2">
        <h2 id="agreement" className="panel-label flex-1">
          Agreement
        </h2>
        {live && zone ? (
          <span className="text-xs text-muted">
            from {dayWithYear(tenantDay(live.createdAt, zone))}
          </span>
        ) : null}
        {mayEdit ? <EditButton what="the agreement" onClick={() => setEditing(true)} /> : null}
      </div>
      {editing ? (
        <Popover title="Agreement" onClose={() => setEditing(false)}>
          <AgreementForm venueId={venue.id} live={live} onDone={() => setEditing(false)} />
        </Popover>
      ) : null}
      {agreement.isError ? (
        <p role="alert" className="alert">
          {agreement.error.message}
        </p>
      ) : null}
      <dl className="dl text-sm">
        <dt>Debtor</dt>
        <dd>
          <BilledAs venue={venue} mayManage={mayManage} />
        </dd>
        {live ? <AgreementTerms live={live} /> : null}
      </dl>
      {live ? (
        <TradeDiscounts live={live} />
      ) : (
        <p className="text-sm text-ink-soft">None. Its jobs are standard sales.</p>
      )}
      {history.length > 0 ? (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="link self-start text-sm"
            aria-expanded={showHistory}
            onClick={() => setShowHistory((s) => !s)}
          >
            History ({history.length}) <span aria-hidden="true">{showHistory ? '▴' : '▾'}</span>
          </button>
          {showHistory ? (
            <ul className="text-sm text-ink-soft">
              {history.map((h) => (
                <li key={h.version}>
                  {`Version ${h.version} · ${zone ? dayWithYear(tenantDay(h.createdAt, zone)) : h.createdAt.slice(0, 10)}`}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function AgreementTerms({ live }: { live: Agreement }) {
  return (
    <>
      <dt>Billed as</dt>
      <dd className="font-semibold">{SUPPLY_MODEL_LABELS[live.supplyModel]}</dd>
      <dt>Statement</dt>
      <dd>{SETTLEMENT_LABELS[live.settlement]}</dd>
      <dt>Venue gear sold</dt>
      <dd>{`${bpToPercent(live.venueEquipmentCommissionBp) || '0'}% commission`}</dd>
      <dt>Set-off</dt>
      <dd>{live.setOff ? 'Yes' : 'No'}</dd>
      <dt>RCTI</dt>
      <dd>{live.rcti ? 'Yes' : 'No'}</dd>
      {live.allocation ? (
        <>
          <dt>Allocation</dt>
          <dd>{allocationSummary(live.allocation)}</dd>
        </>
      ) : null}
    </>
  );
}

/** The venue's margin per line type — under resale, its trade discount — as meters out of 50%. */
function TradeDiscounts({ live }: { live: Agreement }) {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="panel-label">
        {live.supplyModel === 'resale' ? 'Trade discount' : 'Margins'}
      </h3>
      {LINE_TYPES.map((t) => (
        <Meter
          key={t}
          label={LINE_TYPE_LABELS[t]}
          fraction={(live.marginsBp[t] ?? 0) / 5000}
          figure={`${bpToPercent(live.marginsBp[t] ?? 0) || '0'}%`}
        />
      ))}
    </div>
  );
}

/** VEN-11 at v1.992: "$20,000.00 of Hire every 12 months, from Wed 1 Jul 2026". */
function allocationSummary(a: AllocationTerms): string {
  const types = a.lineTypes.map((t) => LINE_TYPE_LABELS[t]).join(', ');
  const every = a.periodMonths === 1 ? 'every month' : `every ${a.periodMonths} months`;
  return `${formatMinorUnits(a.amountMinor, 'AUD')} of ${types} ${every}, from ${dayWithYear(a.startsOn)}`;
}

/**
 * VEN-11 at v1.992: this period of the venue's allocation — allowed, used, left — and each job's
 * draw. Money: shown only where the agreement is, to someone who sees prices.
 */
function AllocationBalancePanel({ venueId, zone }: { venueId: string; zone: string | undefined }) {
  const today = zone
    ? tenantDay(Temporal.Now.instant().toString(), zone)
    : Temporal.Now.plainDateISO().toString();
  const balance = useQuery(allocationQuery(venueId, today));
  const jobs = useQuery(jobsQuery);
  if (balance.isError) {
    return (
      <p role="alert" className="alert">
        {balance.error.message}
      </p>
    );
  }
  const b = balance.data;
  if (!b) return null;
  const last = Temporal.PlainDate.from(b.periodEnd).subtract({ days: 1 }).toString();
  const reference = (id: string) => jobs.data?.find((j) => j.id === id)?.reference ?? 'A job';
  const allowed = Number(b.allowanceMinor);
  return (
    <section className="panel flex flex-col gap-2 p-4 text-sm" aria-labelledby="allocation">
      <div className="flex items-center">
        <h2 id="allocation" className="panel-label flex-1">
          Complimentary allocation
        </h2>
        <span className="text-xs text-muted">
          {`${dayWithYear(b.periodStart)} – ${dayWithYear(last)}`}
        </span>
      </div>
      <p className="flex flex-wrap items-baseline gap-2">
        <b className="font-heading text-xl">{formatMinorUnits(b.usedMinor, 'AUD')}</b>
        <span className="text-muted">
          {`of ${formatMinorUnits(b.allowanceMinor, 'AUD')} drawn · ${b.draws.length} venue ${b.draws.length === 1 ? 'job' : 'jobs'} · `}
          <strong>{`${formatMinorUnits(b.leftMinor, 'AUD')} left`}</strong>
        </span>
      </p>
      <Meter fraction={allowed ? Number(b.usedMinor) / allowed : 0} figure="" />
      {b.draws.length > 0 ? (
        <ul className="text-ink-soft">
          {b.draws.map((d) => (
            <li key={d.jobId}>
              <Link to="/jobs/$jobId" params={{ jobId: d.jobId }} className="link">
                {reference(d.jobId)}
              </Link>
              {` · ${formatMinorUnits(d.amountMinor, 'AUD')}`}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function AgreementForm({
  venueId,
  live,
  onDone,
}: {
  venueId: string;
  live: Agreement | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [supplyModel, setSupplyModel] = useState<AgreementSupplyModel>(
    live?.supplyModel ?? 'resale',
  );
  const [margins, setMargins] = useState<Record<LineType, string>>(
    () =>
      Object.fromEntries(LINE_TYPES.map((t) => [t, bpToPercent(live?.marginsBp[t])])) as Record<
        LineType,
        string
      >,
  );
  const [commission, setCommission] = useState(bpToPercent(live?.venueEquipmentCommissionBp));
  const [settlement, setSettlement] = useState<Settlement>(live?.settlement ?? 'per_job');
  const [setOff, setSetOff] = useState(live?.setOff ?? false);
  const [rcti, setRcti] = useState(live?.rcti ?? false);
  // VEN-11 at v1.992: the complimentary allocation.
  const [allocating, setAllocating] = useState(!!live?.allocation);
  const [allocationAmount, setAllocationAmount] = useState(
    live?.allocation ? minorUnitsToDollars(live.allocation.amountMinor) : '',
  );
  const [allocationMonths, setAllocationMonths] = useState(
    String(live?.allocation?.periodMonths ?? 12),
  );
  const [allocationFrom, setAllocationFrom] = useState(live?.allocation?.startsOn ?? '');
  const [allocationTypes, setAllocationTypes] = useState<LineType[]>(
    live?.allocation?.lineTypes ?? ['hire'],
  );
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (input: AgreementInput) => saveAgreement(venueId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agreementQuery(venueId).queryKey });
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const marginsBp: Partial<Record<LineType, number>> = {};
    for (const t of LINE_TYPES) {
      const bp = percentToBp(margins[t]);
      if (bp === null) return setProblem(`${LINE_TYPE_LABELS[t]}: 0 to 100, at most two decimals.`);
      if (bp > 0) marginsBp[t] = bp;
    }
    const commissionBp = percentToBp(commission);
    if (commissionBp === null) return setProblem('Commission: 0 to 100, at most two decimals.');
    let allocation: AllocationTerms | null = null;
    if (allocating) {
      const amountMinor = dollarsToMinorUnits(allocationAmount);
      const months = Number(allocationMonths);
      if (!amountMinor || amountMinor === '0') return setProblem('Allocation: an amount above $0.');
      if (!Number.isInteger(months) || months < 1 || months > 36) {
        return setProblem('Allocation: every 1 to 36 months.');
      }
      if (!allocationFrom) return setProblem('Allocation: the first period’s start.');
      if (allocationTypes.length === 0) return setProblem('Allocation: at least one line type.');
      allocation = {
        amountMinor,
        periodMonths: months,
        startsOn: allocationFrom,
        lineTypes: allocationTypes,
      };
    }
    setProblem(null);
    save.mutate({
      supplyModel,
      marginsBp,
      venueEquipmentCommissionBp: commissionBp,
      settlement,
      setOff,
      rcti,
      allocation,
    });
  }

  const error = problem ?? (save.isError ? save.error.message : null);
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label="Agreement">
      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Supply model</span>
          <select
            className="input"
            value={supplyModel}
            onChange={(e) => setSupplyModel(e.currentTarget.value as AgreementSupplyModel)}
          >
            <option value="resale">{SUPPLY_MODEL_LABELS.resale}</option>
            <option value="direct_bill">{SUPPLY_MODEL_LABELS.direct_bill}</option>
            <option value="agency">Agency (recorded only)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Settlement</span>
          <select
            className="input"
            value={settlement}
            onChange={(e) => setSettlement(e.currentTarget.value as Settlement)}
          >
            <option value="per_job">{SETTLEMENT_LABELS.per_job}</option>
            <option value="monthly">{SETTLEMENT_LABELS.monthly}</option>
          </select>
        </label>
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-xs text-ink-soft">Margin by line type (%)</legend>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {LINE_TYPES.map((t) => (
            <Field
              key={t}
              label={LINE_TYPE_LABELS[t]}
              inputMode="decimal"
              placeholder="0"
              value={margins[t]}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setMargins((m) => ({ ...m, [t]: value }));
              }}
            />
          ))}
        </div>
      </fieldset>
      <Field
        label="Venue gear commission (%)"
        inputMode="decimal"
        placeholder="0"
        value={commission}
        onChange={(e) => setCommission(e.currentTarget.value)}
      />
      <div className="flex flex-wrap gap-6 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={setOff}
            onChange={(e) => setSetOff(e.currentTarget.checked)}
          />
          Set-off
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={rcti}
            onChange={(e) => setRcti(e.currentTarget.checked)}
          />
          RCTI (recorded only)
        </label>
      </div>
      <fieldset className="flex flex-col gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allocating}
            onChange={(e) => setAllocating(e.currentTarget.checked)}
          />
          Complimentary allocation
        </label>
        {allocating ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                label="Amount ($)"
                inputMode="decimal"
                value={allocationAmount}
                onChange={(e) => setAllocationAmount(e.currentTarget.value)}
              />
              <Field
                label="Every (months)"
                inputMode="numeric"
                value={allocationMonths}
                onChange={(e) => setAllocationMonths(e.currentTarget.value)}
              />
              <Field
                label="From"
                type="date"
                value={allocationFrom}
                onChange={(e) => setAllocationFrom(e.currentTarget.value)}
              />
            </div>
            <div className="flex flex-wrap gap-4 text-sm" role="group" aria-label="Covers">
              {LINE_TYPES.map((t) => (
                <label key={t} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allocationTypes.includes(t)}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setAllocationTypes((types) =>
                        on ? [...types, t] : types.filter((x) => x !== t),
                      );
                    }}
                  />
                  {LINE_TYPE_LABELS[t]}
                </label>
              ))}
            </div>
          </>
        ) : null}
      </fieldset>
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
